import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { joinWaitlist } from "@/lib/api";

/**
 * WaitlistFormCore — the bare form UI (name / email / consent / submit).
 * Reused by both the homepage waitlist section and the /why page CTA block.
 */
export function WaitlistFormCore({ testIdPrefix = "waitlist" }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await joinWaitlist({
        email: email.trim(),
        name: name.trim() || null,
        consent_marketing: consent,
        source: "landing_html",
      });
      setDone(true);
      toast.success("You're in. We'll be in touch.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Couldn't save your spot. Try again?";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div
        className="flex flex-col items-center text-center py-6"
        data-testid={`${testIdPrefix}-success`}
      >
        <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FFF4EC] text-[#9A3F25] mb-4">
          <Check size={26} strokeWidth={2} />
        </span>
        <h3 className="font-display text-2xl sm:text-3xl font-medium text-[#1C1917]">
          You&apos;re in.
        </h3>
        <p className="mt-2 text-[#44403C]">We&apos;ll be in touch.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-4"
      data-testid={`${testIdPrefix}-form`}
    >
      <div>
        <label
          htmlFor={`${testIdPrefix}-name`}
          className="block text-sm font-medium text-[#44403C] mb-1.5"
        >
          Name
        </label>
        <input
          id={`${testIdPrefix}-name`}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          className="w-full px-4 py-3 rounded-xl bg-white border border-[#F1D9C6] focus:border-[#EC6A4A] focus:ring-2 focus:ring-[#EC6A4A]/20 outline-none text-[#1C1917] placeholder:text-[#A8A29E] transition-colors"
          data-testid={`${testIdPrefix}-name-input`}
        />
      </div>
      <div>
        <label
          htmlFor={`${testIdPrefix}-email`}
          className="block text-sm font-medium text-[#44403C] mb-1.5"
        >
          Email
        </label>
        <input
          id={`${testIdPrefix}-email`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourdomain.com"
          className="w-full px-4 py-3 rounded-xl bg-white border border-[#F1D9C6] focus:border-[#EC6A4A] focus:ring-2 focus:ring-[#EC6A4A]/20 outline-none text-[#1C1917] placeholder:text-[#A8A29E] transition-colors"
          data-testid={`${testIdPrefix}-email-input`}
        />
      </div>
      <label className="flex items-start gap-3 cursor-pointer select-none pt-1">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 w-4 h-4 accent-[#EC6A4A] cursor-pointer"
          data-testid={`${testIdPrefix}-consent-checkbox`}
        />
        <span className="text-sm text-[#44403C] leading-relaxed">
          Yes, send me product updates and early-access invites. No spam —
          promise.
        </span>
      </label>
      <button
        type="submit"
        disabled={submitting}
        className="cta-pill cta-primary w-full justify-center mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid={`${testIdPrefix}-submit-btn`}
      >
        {submitting ? "Joining…" : "Join waitlist"}
        {!submitting && <ArrowRight size={16} />}
      </button>
    </form>
  );
}
